import React, { useState, useEffect } from "react";
import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Slugs already hardcoded below as static menu links. Any published Course CMS
// page with a slug NOT in this set is appended dynamically to its menu so newly
// added courses show up without needing another code deploy.
const HARDCODED_COURSE_SLUGS = new Set([
  "diploma-mastery-fm", "diploma-facilities-management", "diploma-fm-administration",
  "diploma-fm-operations", "cfm-training", "sfp-training",
  "certified-maintenance-reliability-professional", "ai-ml-training",
  "certified-ai-professional", "certified-ai-testing-professional",
  "certified-hr-professional", "certified-hr-manager", "smart-finance-manager",
  "corporate-financial-reporting-accounting", "certified-international-procurement-auditor",
  "certified-international-procurement-professional", "certified-international-procurement-manager",
  "certified-purchasing-professional", "category-management-procurements",
  "pmp-certification", "certified-supply-chain-professional",
  "certified-logistics-supply-chain-management-professional",
  "certified-professional-supply-management", "logistics-courses",
  "certified-planning-inventory-management", "certified-strategic-manager",
  "certified-business-administrator", "event-management-course",
  "lean-six-sigma-yellow-belt-certification", "lean-six-sigma-green-belt-certification",
  "lean-six-sigma-black-belt-certification", "lean-six-sigma-master-black-certification",
  "leadership-management-engineers", "diversity-culture-training",
  "negotiation-skills-course", "certified-training-manager-course",
  "train-the-trainer-course", "personality-development-course",
  "business-communication-course", "business-english-course",
  "advanced-english-conversation-course", "spoken-english-training-course",
  "business-presentation-skills", "business-writing-course", "business-grammar-course",
  "ielts-preparation-training-course", "sat-training-course", "toefl-course",
  "spoken-arabic-course",
]);

// About Us dropdown page slugs already hardcoded below. Any published About Us
// CMS page with a slug NOT in this set is appended dynamically to the About
// menu. "education-career-counselling" is excluded too even though it isn't
// in the About dropdown — it already has its own top-level "Counselling" nav
// link, so it must not also be auto-appended under About.
const HARDCODED_ABOUT_SLUGS = new Set(["about", "chairmans-message", "our-story", "education-career-counselling"]);

// Renders a hardcoded course-menu <li> only if its Course CMS page still
// exists and is published. validSlugs is null while the initial fetch is
// still in flight (menu renders unfiltered so nothing flashes/disappears on
// first paint) and becomes a Set once loaded — any slug missing from it means
// the admin deleted or unpublished that course page, so the dead link that
// used to 404 is simply not rendered instead.
function CourseMenuLink({ slug, validSlugs, children }) {
  if (validSlugs && !validSlugs.has(slug)) return null;
  return (
    <li>
      <a href={`/${slug}`}>{children}</a>
    </li>
  );
}

function Head() {
  const [extraCourses, setExtraCourses] = useState([]);
  const [validCourseSlugs, setValidCourseSlugs] = useState(null);
  const [extraAboutPages, setExtraAboutPages] = useState([]);

  useEffect(() => {
    axios.get(`${apiUrl}/api/public/course-pages`)
      .then((res) => {
        if (res.data.success) {
          setValidCourseSlugs(new Set(res.data.coursePages.map((c) => c.slug)));
          setExtraCourses(res.data.coursePages.filter((c) => !HARDCODED_COURSE_SLUGS.has(c.slug)));
        }
      })
      .catch(() => {});
    axios.get(`${apiUrl}/api/public/about-pages`)
      .then((res) => {
        if (res.data.success) {
          setExtraAboutPages(res.data.aboutPages.filter((p) => !HARDCODED_ABOUT_SLUGS.has(p.slug)));
        }
      })
      .catch(() => {});
  }, []);

  // The theme's mobile hamburger toggle (assets/js/main.js: mainNav) is
  // injected via jQuery DOM manipulation rather than being part of the
  // template markup, and main.js only runs once, at initial page load, via
  // $(".ak-munu_toggle").on("click", ...) — a direct (non-delegated) bind.
  // Depending on script/mount load order, jQuery sometimes finds the nav
  // already rendered by React and successfully appends + binds its own
  // toggle before this effect runs, leaving two competing click handlers
  // (jQuery's slideToggle-based one and this plain-JS one) on the same node,
  // which fire together and cancel each other out. Cloning the toggle node
  // before binding strips any handler already attached to it (jQuery's data/
  // listeners are keyed to the original node, not carried over by cloning),
  // so this effect always ends up as the sole listener regardless of timing.
  useEffect(() => {
    const nav = document.querySelector(".ak-nav");
    const navList = document.querySelector(".ak-nav_list");
    if (!nav || !navList) return;

    let toggle = nav.querySelector(".ak-munu_toggle");
    if (!toggle) {
      toggle = document.createElement("span");
      toggle.className = "ak-munu_toggle";
      toggle.appendChild(document.createElement("span"));
      nav.appendChild(toggle);
    }
    const freshToggle = toggle.cloneNode(true);
    toggle.replaceWith(freshToggle);
    toggle = freshToggle;

    const handleToggle = () => {
      toggle.classList.toggle("ak-toggle_active");
      navList.style.display = navList.style.display === "block" ? "none" : "block";
    };
    toggle.addEventListener("click", handleToggle);

    const dropdownCleanups = [];
    document.querySelectorAll(".menu-item-has-children").forEach((item) => {
      let dropdownToggle = item.querySelector(":scope > .ak-munu_dropdown_toggle");
      if (!dropdownToggle) {
        dropdownToggle = document.createElement("span");
        dropdownToggle.className = "ak-munu_dropdown_toggle";
        item.appendChild(dropdownToggle);
      }
      const freshDropdownToggle = dropdownToggle.cloneNode(true);
      dropdownToggle.replaceWith(freshDropdownToggle);
      dropdownToggle = freshDropdownToggle;

      const submenu = item.querySelector(":scope > ul");
      const handleDropdown = (e) => {
        e.stopPropagation();
        dropdownToggle.classList.toggle("active");
        item.classList.toggle("active");
        if (submenu) submenu.style.display = submenu.style.display === "block" ? "none" : "block";
      };
      dropdownToggle.addEventListener("click", handleDropdown);
      dropdownCleanups.push(() => dropdownToggle.removeEventListener("click", handleDropdown));
    });

    return () => {
      toggle.removeEventListener("click", handleToggle);
      dropdownCleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  // FM Courses has no sub-menu, so its dynamic extras stay flat. Management
  // Courses and Key Courses are organised into sub-menus (Purchase and
  // Procurements, Soft Skills, ...); a course tagged with a sub_category is
  // nested into that matching sub-menu via extrasForSub, while one left
  // without a sub_category (shouldn't normally happen for these two menus,
  // but is handled gracefully) falls back to a flat item via extrasFor.
  const extrasFor = (menu) => extraCourses.filter((c) => c.menu_category === menu && !c.sub_category);
  const extrasForSub = (menu, sub) => extraCourses.filter((c) => c.menu_category === menu && c.sub_category === sub);
  return (
    <>
      {/* Start Header Section */}
      <header className="ak-site_header ak-style1 ak-sticky_header">
        <div className="ak-main_header">
          <div className="container">
            <div className="ak-main_header_in">
              <div className="ak-main-header-left">
                <a className="ak-site_branding" href="/">
                  <img
                    src="assets/img/logo.png"
                    alt="..."
                    style={{ width: "100%", maxWidth: 190, height: "auto" }}
                  />
                </a>
              </div>
              <div className="ak-main-header-center">
                <div className="ak-nav ak-medium">
                  <ul className="ak-nav_list">
                    <li>
                      <a href="/">Home</a>
                    </li>
                    <li className="menu-item-has-children">
                      <a href="#">About</a>
                      <ul>
                        <li>
                          <a href="/about">About IFM Edu Academy</a>
                        </li>
                        <li>
                          <a href="/chairmans-message">Chairman's Message</a>
                        </li>
                        <li>
                          <a href="/our-story">Our Story</a>
                        </li>
                         <li>
                          <a href="https://drmanojkurup.com/" target="_blank" rel="noopener noreferrer">Meet the Trainer</a>
                        </li>
                        {extraAboutPages.map((p) => (
                          <li key={p.id}>
                            <a href={`/${p.slug}`}>{p.title}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="menu-item-has-children ak-menu-two-col">
                      <a href="#">FM Courses</a>
                      <ul>
                        <li className="menu-item-has-children">
                          <a href="#">International FM Diploma Programme</a>
                          <ul>
                            <CourseMenuLink slug="diploma-mastery-fm" validSlugs={validCourseSlugs}>
                              Diploma of Mastery in Facilities Management (DMFM)
                            </CourseMenuLink>
                            <CourseMenuLink slug="diploma-fm-administration" validSlugs={validCourseSlugs}>
                              Diploma in Facilities Administration (DFA)
                            </CourseMenuLink>
                            <CourseMenuLink slug="diploma-fm-operations" validSlugs={validCourseSlugs}>
                              Diploma in Facilities Operations and Maintenance (DOM)
                            </CourseMenuLink>
                            {extrasForSub("FM Courses", "International FM Diploma Programme").map((c) => (
                              <li key={c.id}>
                                <a href={`/${c.slug}`}>{c.title}</a>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <CourseMenuLink slug="diploma-facilities-management" validSlugs={validCourseSlugs}>
                          Diploma in Facilities Management (DFM)
                        </CourseMenuLink>
                        <CourseMenuLink slug="cfm-training" validSlugs={validCourseSlugs}>
                          Certified Facility Manager (CFM) Training
                        </CourseMenuLink>
                        <CourseMenuLink slug="sfp-training" validSlugs={validCourseSlugs}>
                          Sustainability Facility Professional (SFP) training
                        </CourseMenuLink>
                        <CourseMenuLink slug="certified-maintenance-reliability-professional" validSlugs={validCourseSlugs}>
                          Certified Maintenance and Reliability Professional
                          (CMRP)
                        </CourseMenuLink>
                        {extrasFor("FM Courses").map((c) => (
                          <li key={c.id}>
                            <a href={`/${c.slug}`}>{c.title}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="menu-item-has-children">
                      <a href="#">Special Courses</a>
                      <ul>
                        {extrasFor("Special Courses").map((c) => (
                          <li key={c.id}>
                            <a href={`/${c.slug}`}>{c.title}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li>
                      <a href="/testimonials">Testimonials</a>
                    </li>
                    <li>
                      <a href="/press-and-media">Press &amp; Media</a>
                    </li>
                    <li>
                      <a href="/contacts">Contacts</a>
                    </li>
                      <li>
                      <a href="https://ifmedu.in/apply/register.php">Register</a>
                    </li>
                    <li className="menu-item-has-children">
                      <a href="#">Discover</a>
                      <ul>
                        <li className="menu-item-has-children">
                          <a href="#">Management Courses</a>
                          <ul>
                            <li className="menu-item-has-children">
                              <a href="#">Artificial Intelligence Course</a>
                              <ul>
                                <CourseMenuLink slug="ai-ml-training" validSlugs={validCourseSlugs}>
                                  Artificial Intelligence and Machine Learning
                                  Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-ai-professional" validSlugs={validCourseSlugs}>
                                  Certified Artificial Intelligence Professional
                                  (CAIP)
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-ai-testing-professional" validSlugs={validCourseSlugs}>
                                  Certified AI Testing Professional (CAITP)
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Artificial Intelligence Course").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Human Resources Management Courses</a>
                              <ul>
                                <CourseMenuLink slug="certified-hr-professional" validSlugs={validCourseSlugs}>
                                  Certified Human Resources Professional(CHRP)-CPD
                                  UK
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-hr-manager" validSlugs={validCourseSlugs}>
                                  Certified Human Resource Manager (CHRM) Training
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Human Resources Management Courses").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Finance and Accounting</a>
                              <ul>
                                <CourseMenuLink slug="smart-finance-manager" validSlugs={validCourseSlugs}>
                                  Smart Finance for Non-Finance Professionals
                                </CourseMenuLink>
                                <CourseMenuLink slug="corporate-financial-reporting-accounting" validSlugs={validCourseSlugs}>
                                  Corporate Financial Reporting &amp; Accounting
                                  Course (CFRA)
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Finance and Accounting").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Purchase and Procurements</a>
                              <ul>
                                <CourseMenuLink slug="certified-international-procurement-auditor" validSlugs={validCourseSlugs}>
                                  Certified International Procurement Auditor
                                  (CIPA)
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-international-procurement-professional" validSlugs={validCourseSlugs}>
                                  Certified International Procurement Professional
                                  (CIPP)
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-international-procurement-manager" validSlugs={validCourseSlugs}>
                                  Certified International Procurement Manager
                                  (CIPM)
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-purchasing-professional" validSlugs={validCourseSlugs}>
                                  Certified Purchasing Professional (CPP) Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="category-management-procurements" validSlugs={validCourseSlugs}>
                                  Category Management in Procurements
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Purchase and Procurements").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Project Management</a>
                              <ul>
                                <CourseMenuLink slug="pmp-certification" validSlugs={validCourseSlugs}>
                                  PMP Certification –PMI Accredited
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Project Management").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Logistics and Supply Chain</a>
                              <ul>
                                <CourseMenuLink slug="certified-supply-chain-professional" validSlugs={validCourseSlugs}>
                                  Certified Supply Chain Professional (CSCP)
                                  Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-logistics-supply-chain-management-professional" validSlugs={validCourseSlugs}>
                                  Certified Logistics &amp; Supply chain
                                  Management Professional (CLSCMP) Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-professional-supply-management" validSlugs={validCourseSlugs}>
                                  Certified Professional in Supply Management
                                  (CPSM) Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="logistics-courses" validSlugs={validCourseSlugs}>
                                  Logistics Courses
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-planning-inventory-management" validSlugs={validCourseSlugs}>
                                  Certified Planning &amp; Inventory Management
                                  (CPIM) Certification
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Logistics and Supply Chain").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Business Management</a>
                              <ul>
                                <CourseMenuLink slug="certified-strategic-manager" validSlugs={validCourseSlugs}>
                                  Certified Strategic Manager (UK Certification)
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-business-administrator" validSlugs={validCourseSlugs}>
                                  Certified Business Administrator (UK
                                  Certification)
                                </CourseMenuLink>
                                <CourseMenuLink slug="event-management-course" validSlugs={validCourseSlugs}>
                                  Event Management Course
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Business Management").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li className="menu-item-has-children">
                              <a href="#">Quality &amp; Process Management</a>
                              <ul>
                                <CourseMenuLink slug="lean-six-sigma-yellow-belt-certification" validSlugs={validCourseSlugs}>
                                  Lean Six Sigma Yellow Belt Certification
                                </CourseMenuLink>
                                <CourseMenuLink slug="lean-six-sigma-green-belt-certification" validSlugs={validCourseSlugs}>
                                  Lean Six Sigma Green belt Certifications
                                </CourseMenuLink>
                                <CourseMenuLink slug="lean-six-sigma-black-belt-certification" validSlugs={validCourseSlugs}>
                                  Lean Six Sigma Black belt Certification
                                </CourseMenuLink>
                                <CourseMenuLink slug="lean-six-sigma-master-black-certification" validSlugs={validCourseSlugs}>
                                  Lean Six Sigma Master Black Belt Certification
                                  Training
                                </CourseMenuLink>
                                {extrasForSub("Management Courses", "Quality & Process Management").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            {extrasFor("Management Courses").map((c) => (
                              <li key={c.id}>
                                <a href={`/${c.slug}`}>{c.title}</a>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li className="menu-item-has-children">
                          <a href="#">Key courses</a>
                          <ul>
                            <li>
                              <a href="#">Soft Skills</a>
                              <ul>
                                <CourseMenuLink slug="leadership-management-engineers" validSlugs={validCourseSlugs}>
                                  Leadership and Management for Engineers
                                </CourseMenuLink>
                                <CourseMenuLink slug="diversity-culture-training" validSlugs={validCourseSlugs}>
                                  Diversity &amp; Culture training
                                </CourseMenuLink>
                                <CourseMenuLink slug="negotiation-skills-course" validSlugs={validCourseSlugs}>
                                  Negotiation Skills Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="certified-training-manager-course" validSlugs={validCourseSlugs}>
                                  Certified Training Manager Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="train-the-trainer-course" validSlugs={validCourseSlugs}>
                                  Train the Trainer Course (TTT)
                                </CourseMenuLink>
                                <CourseMenuLink slug="personality-development-course" validSlugs={validCourseSlugs}>
                                  Personality Development and public speaking
                                  course
                                </CourseMenuLink>
                                {extrasForSub("Key Courses", "Soft Skills").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            <li>
                              <a href="#">Language Training Courses</a>
                              <ul>
                                <CourseMenuLink slug="business-communication-course" validSlugs={validCourseSlugs}>
                                  Excellent Business Communication Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="business-english-course" validSlugs={validCourseSlugs}>
                                  Business English Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="advanced-english-conversation-course" validSlugs={validCourseSlugs}>
                                  Advanced English Conversation Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="spoken-english-training-course" validSlugs={validCourseSlugs}>
                                  Spoken English Training Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="business-presentation-skills" validSlugs={validCourseSlugs}>
                                  Business Presentation Skills
                                </CourseMenuLink>
                                <CourseMenuLink slug="business-writing-course" validSlugs={validCourseSlugs}>
                                  Business Writing Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="business-grammar-course" validSlugs={validCourseSlugs}>
                                  Business Grammar Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="ielts-preparation-training-course" validSlugs={validCourseSlugs}>
                                  IELTS Preparation &amp; Training Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="sat-training-course" validSlugs={validCourseSlugs}>
                                  SAT Training Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="toefl-course" validSlugs={validCourseSlugs}>
                                  TOEFL Course
                                </CourseMenuLink>
                                <CourseMenuLink slug="spoken-arabic-course" validSlugs={validCourseSlugs}>
                                  Spoken Arabic Course
                                </CourseMenuLink>
                                {extrasForSub("Key Courses", "Language Training Courses").map((c) => (
                                  <li key={c.id}>
                                    <a href={`/${c.slug}`}>{c.title}</a>
                                  </li>
                                ))}
                              </ul>
                            </li>
                            {extrasFor("Key Courses").map((c) => (
                              <li key={c.id}>
                                <a href={`/${c.slug}`}>{c.title}</a>
                              </li>
                            ))}
                          </ul>
                        </li>
                        <li>
                          <a href="/education-career-counselling">Counselling</a>
                        </li>
                        <li>
                          <a href="/blogs">Our Blogs</a>
                        </li>
                        <li>
                          <a href="/accreditations">Accreditations</a>
                        </li>
                        <li>
                          <a href="/awards">Awards</a>
                        </li>
                        <li>
                          <a href="/our-infrastructure">Our infrastructure</a>
                        </li>
                        <li>
                          <a href="/verify-certificate">Verify Certificate</a>
                        </li>
                        <li>
                          <a href="/photo-gallery">Photo Gallery</a>
                        </li>
                        <li>
                          <a href="/video-gallery">Video Gallery</a>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="ak-main-header-right">
                <a
                  href="https://lms.ifmedu.in"
                  target="_blank"
                  className="common-btn"
                >
                  <span style={{ color: "white" }}>Login</span>
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width={15}
                      height={15}
                      viewBox="0 0 15 15"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_201_978435345)">
                        <path
                          d="M1.42236 6.99728H13.089M13.089 6.99728L7.48903 1.39728M13.089 6.99728L7.48903 12.5973"
                          stroke="#ffffffff"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_201_978435345">
                          <rect
                            width={14}
                            height={14}
                            fill="white"
                            transform="translate(0.00927734)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="nav-bar-border" />
      </header>
      {/* End Header Section */}
    </>
  );
}

export default Head;
